import React, { useEffect, useState } from "react";
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import DateRangeIcon from "@material-ui/icons/DateRange";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { Dialog, DialogActions, DialogTitle, Typography, WithStyles } from "@material-ui/core";
import { styles } from "./ManageSubscriptionsView.styles";
import * as FilterableSubscriptionRequestAction from "../../../stores/filterableSubscriptionRequests/FilterableSubscriptionRequestAction";
import UniversalTable from "../../components/UniversalTable/UniversalTable";
import { IUniversalTableColumn, IUniversalTableData } from "../../components/UniversalTable/interfaces";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../constants/ToastStatusEnum";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../../selectors/requesting/RequestingSelector";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import { BlueButton } from "../../components/Buttons/BlueButton";
import { useHistory } from "react-router-dom";
import FilterableSubscriptionRequestModel from "../../../models/filterableSubscriptionRequest/FilterableSubscriptionRequestModel";
import { BaseCurrenciesComponent } from "./BaseCurrenciesComponent";

export interface IManageSubscriptionRequestsViewProps extends WithStyles<typeof styles> {}

const ManageSubscriptionRequestsViewPure: React.FunctionComponent<IManageSubscriptionRequestsViewProps> = ({
    classes
}: IManageSubscriptionRequestsViewProps) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const [displayedSubscriptionIndex, setDisplayedSubscriptionIndex] = useState<number>(null);
    const [editedSubscriptionIndex, setEditedSubscriptionIndex] = useState<number>(null);
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState<boolean>(false);
    const [rejectionDialogOpen, setRejectionDialogOpen] = useState<boolean>(false);
    const [detailsDialogOpen, setDetailsDialogOpen] = useState<boolean>(false);
    const history = useHistory();

    useEffect(() => {
        dispatch(FilterableSubscriptionRequestAction.requestAllFilterableSubscriptionRequests());
    }, [dispatch]);

    const requestsFromState = useSelector(state => state.filterableSubscriptionRequests);

    const isRequesting = useSelector(state =>
        selectRequesting(state, [
            FilterableSubscriptionRequestAction.REQUEST_ALL_FILTERABLE_SUBSCRIPTION_REQUESTS
        ])
    );

    let subscriptionRequests: IUniversalTableData;
    if (requestsFromState?.subscriptionRequests) {
        subscriptionRequests = Object.values(requestsFromState?.subscriptionRequests)?.map((request: FilterableSubscriptionRequestModel) => ({
            id: request.id,
            user: request.userInfo[0]["firstName"] + " " + request.userInfo[0]["lastName"],
            userId: request.userInfo[0]["id"],
            subscriptionTypeName: request.subscriptionTypeName,
            startDate: request.startDate,            
            baseCurrencies: request.baseCurrencies,
            state: request.state,
            ...(request.state !== "New" && {
                actionsDisabled: ["confirmSubscription", "rejectSubscription"]
            }),
            productName: request.productName
        }));
    }

    const handleConfirmationClick = index => {
        setEditedSubscriptionIndex(index);
        setConfirmationDialogOpen(true);
    };

    const handleRejectionClick = index => {
        setEditedSubscriptionIndex(index);
        setRejectionDialogOpen(true);
    };

    const handleShowDetails = index => {
        setDisplayedSubscriptionIndex(index);
        setDetailsDialogOpen(true);
    };

    const handleUserSubscriptionsRedirect = index => {
        const path = "/admin/manage-users/" + subscriptionRequests[index].userId + "/subscriptions";
        history.push(path);
    };

    const handleRequestConfirmation = () => {
        dispatch(FilterableSubscriptionRequestAction.confirmFilterableSubscriptionRequest(subscriptionRequests[editedSubscriptionIndex].id))
            .then(() => {
                dispatch(FilterableSubscriptionRequestAction.requestAllFilterableSubscriptionRequests());
                enqueueSnackbar("The subscription request " + (subscriptionRequests[editedSubscriptionIndex].id) + " was approved successfully.",
                    {
                        variant: ToastStatusEnum.Info
                    }
                );
            })
            .catch(error => 
                enqueueSnackbar("The approval of subscription request " + (subscriptionRequests[editedSubscriptionIndex].id) + " was not successful: " + error, 
                {
                    variant: ToastStatusEnum.Error
                })
            );
        setConfirmationDialogOpen(false);
    };

    const handleRequestRejection = () => {
        dispatch(FilterableSubscriptionRequestAction.rejectFilterableSubscriptionRequest(subscriptionRequests[editedSubscriptionIndex].id))
            .then(() => {
                dispatch(FilterableSubscriptionRequestAction.requestAllFilterableSubscriptionRequests());
                enqueueSnackbar("The subscription request " + (subscriptionRequests[editedSubscriptionIndex].id) + " was rejected.",
                    {
                        variant: ToastStatusEnum.Info
                    }
                );
            })
            .catch(error => 
                enqueueSnackbar("The rejection of subscription request " + (subscriptionRequests[editedSubscriptionIndex].id) + " was not successful: " + error, 
                {
                    variant: ToastStatusEnum.Error
                })
            );
        setRejectionDialogOpen(false);
    };

    const columns: IUniversalTableColumn[] = [
        {
            type: "text",
            title: "Id",
            field: "id"
        },
        {
            type: "text",
            title: "User",
            field: "user"
        },
        {
            type: "text",
            title: "Subscription type",
            field: "subscriptionTypeName"
        },
        {
            type: "text",
            title: "Product name",
            field: "productName"
        },
        {
            type: "date",
            title: "Start date",
            field: "startDate"
        },
        {
            type: "link",
            title: "Assets",
            linkTitle: "Show assets",
            onClick: (index) => handleShowDetails(index)
        },
        {
            type: "text",
            title: "State",
            field: "state"
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "confirmSubscription",
                    icon: <ThumbUpIcon />,
                    tooltip: "Confirm subscription request",
                    onClick: index => handleConfirmationClick(index),
                    scope: "subscriptions.write",
                    disabledTooltip: "Confirm the request only if status is 'New'"
                },
                {
                    name: "rejectSubscription",
                    icon: <ThumbDownIcon />,
                    tooltip: "Reject subscription request",
                    onClick: index => handleRejectionClick(index),
                    scope: "subscriptions.write",
                    disabledTooltip: "Reject the request only if status is 'New'"
                },
                {
                    name: "goToSubscriptions",
                    icon: <DateRangeIcon />,
                    tooltip: "Go to subscriptions",
                    onClick: index => handleUserSubscriptionsRedirect(index),
                    scope: "subscriptions.write"
                }
            ]
        }
    ];
    
    return (
        <CenteredContainer>
            <Breadcrumbs
                items={[{ title: t("admin.adminPanel"), href: "/admin" }, { title: "Manage subscription requests" }]}
            />
            <div className={classes.title}>
                <Typography variant="h4">Manage subscription requests</Typography>
            </div>
            <Typography variant="caption">Please add users via manage subscription requests</Typography>
            <UniversalTable columns={columns} data={subscriptionRequests} isLoading={isRequesting} searchable />

            <Dialog open={confirmationDialogOpen}>
                <DialogTitle id="confirm-request-approval-dialog">
                    Are you sure you want to confirm this subscription request{(subscriptionRequests && editedSubscriptionIndex) ? " with the id " + subscriptionRequests[editedSubscriptionIndex].id : ""}?
                </DialogTitle>
                <DialogActions>
                    <BlueButton onClick={handleRequestConfirmation}>Yes</BlueButton>
                    <BlueButton onClick={() => setConfirmationDialogOpen(false)}>No</BlueButton>
                </DialogActions>
            </Dialog>

            <Dialog open={rejectionDialogOpen}>
                <DialogTitle id="confirm-request-approval-dialog">
                    Are you sure you want to reject this subscription request{(subscriptionRequests && editedSubscriptionIndex) ? " with the id " + subscriptionRequests[editedSubscriptionIndex].id : ""}?
                </DialogTitle>
                <DialogActions>
                    <BlueButton onClick={handleRequestRejection}>Yes</BlueButton>
                    <BlueButton onClick={() => setRejectionDialogOpen(false)}>No</BlueButton>
                </DialogActions>
            </Dialog>

            {displayedSubscriptionIndex !== null && <BaseCurrenciesComponent currencies={subscriptionRequests[displayedSubscriptionIndex]["baseCurrencies"].map(currency => currency.name).join(", ")} detailsDialogOpen={detailsDialogOpen} setDetailsDialogOpen={setDetailsDialogOpen}/>}

        </CenteredContainer>
    );
};

export const ManageSubscriptionRequestsView = withStyles(styles)(ManageSubscriptionRequestsViewPure);
